const CALLS_LAYER = 'CallsLayer';
const ALERTS_LAYER = 'AlertsLayer';
const W3W_LAYER = 'W3WLayer';
const PURE_WIRELESS_LAYER = 'PureWireless';
/**
 * Creates singleton storage for layer data - designed to be used with DeckGL.
 * Since DeckGL uses a reactive pattern, the layers array needs to be set with
 * a new array and layer instances for every change. DeckGL internally optimizes
 * renders using its own diffing mechanism. Use this store to keep all layers
 * needed to draw a single visualization.
 */
const storeLayers = () => {
  let index = 0;
  const store = {};

  return {
    /**
     * Return all of the internal state in the store.
     */
    get() {
      return store;
    },

    /**
     * Get all layers in the store sorted by priority.
     * CALLS and ALERTS layers are always rendered on top.
     *
     * Note: DeckOverlay.setLayers() REVERSES the layer order for Google Maps,
     * so we sort CALLS/ALERTS FIRST here. After reversal, they'll be LAST (on top).
     *
     * Priority order (highest first, will be reversed for Google Maps):
     * 4 = Calls layer (first in array → last after reversal → rendered on top)
     * 3 = Alerts layer
     * 2 = W3W layer
     * 1 = PureWireless layer (below regular layers — indoor maps behind markers/overlays)
     * 0 = Regular layers (jurisdiction, temp-location, etc.)
     */
    getAll() {
      const getLayerPriority = layerId => {
        if (layerId === CALLS_LAYER) return 4;
        if (layerId === ALERTS_LAYER) return 3;
        if (layerId === W3W_LAYER) return 2;
        if (layerId === PURE_WIRELESS_LAYER) return 1;
        return 0;
      };

      const sorted = Object.values(store)
        .sort((a, b) => {
          const priorityA = getLayerPriority(a.layer?.id);
          const priorityB = getLayerPriority(b.layer?.id);

          // Sort by priority DESCENDING (higher priority = earlier in array)
          // This is because DeckOverlay reverses the order for Google Maps
          if (priorityA !== priorityB) {
            return priorityB - priorityA;
          }

          // For same priority, sort by DESCENDING insertion order (later inserts first)
          // This matches the original behavior where later-inserted layers end up on top after Google reversal
          return (b?.index || 0) - (a?.index || 0);
        })
        .map(o => o?.layer)
        .filter(o => Boolean(o));
      return [...sorted];
    },

    /**
     * Save a layer to the store.
     * @param {number} id
     * @param {DeckGL.Layer} layer
     */
    save(layer) {
      const id = layer?.id;
      if (!id) {
        return;
      }
      if (!store[id]) {
        index += 1;
        store[id] = { index, layer };
      } else {
        const prev = store[id];
        store[id] = { index: prev.index, layer };
      }
    },

    /**
     * Save a list of layers to the store.
     * @param {number} id
     * @param {DeckGL.Layer} layer
     */
    saveGroup(layers) {
      layers.forEach(layer => {
        this.save(layer);
      });
    },

    deleteLayer(layerID) {
      if (store[layerID]) {
        delete store[layerID];
      }
    },

    deleteAll() {
      Object.getOwnPropertyNames(store).forEach(layer => {
        delete store[layer];
      });
    },
  };
};

export default storeLayers();
